import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, AlertTriangle, CheckCircle, ArrowRight, Search, 
  Clock, Mail, Globe, Phone, Users, Building2, Coins,
  Calculator, AlertCircle, Eye, ShieldCheck, ChevronRight,
  CreditCard, KeyRound, UserX, Sparkles, Ban, Flag, ExternalLink,
  Share2, Twitter, Facebook, MessageCircle, Copy, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SITE_URL = 'https://actionfraudone.com';

// Share button component
const ShareButton = ({ icon: Icon, label, onClick, className, bgColor }) => (
  <button
    onClick={onClick}
    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 ${className}`}
    style={{ backgroundColor: bgColor }}
    data-testid={`share-${label.toLowerCase()}-btn`}
  >
    <Icon size={16} />
    <span className="hidden sm:inline">{label}</span>
  </button>
);

// Category icons for breakdown
const getCategoryIcon = (type) => {
  const iconMap = {
    'urgency': { icon: Clock, color: 'text-orange-400' },
    'payment_extraction': { icon: CreditCard, color: 'text-red-400' },
    'crypto': { icon: Coins, color: 'text-yellow-400' },
    'card_details': { icon: CreditCard, color: 'text-red-500' },
    'otp_banking': { icon: KeyRound, color: 'text-red-500' },
    'impersonation': { icon: UserX, color: 'text-purple-400' },
    'too_good_to_be_true': { icon: Sparkles, color: 'text-pink-400' },
    'grammar_issues': { icon: AlertCircle, color: 'text-gray-400' },
    'suspicious_domain': { icon: Globe, color: 'text-orange-400' },
    'user_report': { icon: Flag, color: 'text-blue-400' },
  };
  return iconMap[type] || { icon: AlertTriangle, color: 'text-gold' };
};

// Risk level styling
const getRiskStyles = (level) => {
  switch (level) {
    case 'High Risk':
      return { bg: 'bg-red-500', text: 'text-red-500', border: 'border-red-500/30', bgLight: 'bg-red-500/10' };
    case 'Moderate Risk':
      return { bg: 'bg-orange-500', text: 'text-orange-500', border: 'border-orange-500/30', bgLight: 'bg-orange-500/10' };
    case 'Needs Expert Review':
      return { bg: 'bg-yellow-500', text: 'text-yellow-500', border: 'border-yellow-500/30', bgLight: 'bg-yellow-500/10' };
    case 'Low Risk':
      return { bg: 'bg-emerald-500', text: 'text-emerald-500', border: 'border-emerald-500/30', bgLight: 'bg-emerald-500/10' };
    default:
      return { bg: 'bg-gray-500', text: 'text-gray-400', border: 'border-gray-500/30', bgLight: 'bg-gray-500/10' };
  }
};

const getRiskIcon = (level) => {
  switch (level) {
    case 'High Risk': return <AlertTriangle className="w-12 h-12 text-white" />;
    case 'Moderate Risk': return <AlertCircle className="w-12 h-12 text-white" />;
    case 'Needs Expert Review': return <Eye className="w-12 h-12 text-white" />;
    case 'Low Risk': return <ShieldCheck className="w-12 h-12 text-white" />;
    default: return <Shield className="w-12 h-12 text-white" />;
  }
};

const getEntityIcon = (type) => {
  switch (type) {
    case 'domain': return Globe;
    case 'email': return Mail;
    case 'phone': return Phone;
    case 'company': return Building2;
    case 'crypto_wallet': return Coins;
    case 'social_handle': return Users;
    default: return Shield;
  }
};

// SCAM_TYPES for report form
const SCAM_TYPES = [
  "Investment Scam", "Crypto Fraud", "Romance Scam", "Job Scam", 
  "Phishing", "Impersonation", "Advance Fee Fraud", "Tech Support Scam",
  "Invoice Fraud", "Lottery/Prize Scam", "Other"
];

export const ScamCheckPage = () => {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportForm, setReportForm] = useState({
    scam_type: '',
    description: '',
    country: '',
    amount_lost: '',
    contact_email: ''
  });
  const [submittingReport, setSubmittingReport] = useState(false);
  const [copied, setCopied] = useState(false);

  // Share functions
  const shareUrl = `${SITE_URL}/scam-check/${slug}`;
  
  const handleShare = (platform) => {
    const entityName = pageData?.entity?.name || slug;
    const riskLevel = pageData?.entity?.risk_level || 'Unknown';
    const shareText = `⚠️ SCAM WARNING: ${entityName} has been flagged as "${riskLevel}" on ActionFraudOne Fraud Intelligence. Check the full report:`;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/scam-check/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('not_found');
          } else {
            throw new Error('Failed to load');
          }
          return;
        }
        const data = await response.json();
        setPageData(data);
      } catch (err) {
        setError('error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!reportForm.scam_type) {
      toast.error('Please select a scam type');
      return;
    }

    setSubmittingReport(true);
    try {
      const response = await fetch(`${API_URL}/api/scam-intelligence/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_slug: slug,
          entity_name: pageData.entity.name,
          entity_type: pageData.entity.type,
          ...reportForm
        })
      });

      if (!response.ok) throw new Error('Failed to submit');
      
      toast.success('Report submitted. Thank you for helping protect others.');
      setShowReportForm(false);
      setReportForm({ scam_type: '', description: '', country: '', amount_lost: '', contact_email: '' });
      
      // Refresh page data
      const refreshed = await fetch(`${API_URL}/api/scam-check/${slug}`);
      if (refreshed.ok) {
        setPageData(await refreshed.json());
      }
    } catch (err) {
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setSubmittingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-gold/30 border-t-gold rounded-full animate-spin mx-auto" />
          <p className="text-gray-400">Loading scam intelligence...</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-black pt-28 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <Shield size={64} className="text-gray-600 mx-auto" />
            <h1 className="text-3xl font-bold text-white">Entity Not Found</h1>
            <p className="text-gray-400">
              This entity hasn't been analyzed yet or the page is not available.
            </p>
            <div className="flex gap-4 justify-center pt-4">
              <Link to="/scam-checker">
                <Button className="bg-gold hover:bg-gold/90 text-black font-bold">
                  <Search className="mr-2" size={18} />
                  Run a Scam Check
                </Button>
              </Link>
              <Link to="/scam-database">
                <Button variant="outline" className="border-gold/40 text-gold hover:bg-gold/10">
                  Browse Database
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!pageData) return null;

  const { entity, report_count, recent_reports, schema_markup, og_meta } = pageData;
  const styles = getRiskStyles(entity.risk_level);
  const EntityIcon = getEntityIcon(entity.type);
  
  // Generate canonical URL
  const canonicalUrl = `${SITE_URL}/scam-check/${slug}`;
  
  // Generate page title and description
  const pageTitle = entity.meta_title || `Is ${entity.name} Legit or a Scam? | ActionFraudOne`;
  const pageDescription = entity.meta_description || `Check if ${entity.name} is a scam. Risk Level: ${entity.risk_level}. See fraud indicators and ${report_count} user reports.`;

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={og_meta?.['og:title'] || pageTitle} />
        <meta property="og:description" content={og_meta?.['og:description'] || pageDescription} />
        <meta property="og:image" content={`${SITE_URL}/lac_logo_gold_transparent.png`} />
        <meta property="og:site_name" content="ActionFraudOne" />
        <meta property="og:locale" content="en_GB" />
        <meta property="article:publisher" content="ActionFraudOne" />
        <meta property="article:section" content="Fraud Intelligence" />
        <meta property="article:tag" content={`${entity.type_label}, Scam Check, Fraud Detection, ${entity.name}`} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={og_meta?.['twitter:title'] || pageTitle} />
        <meta name="twitter:description" content={og_meta?.['twitter:description'] || pageDescription} />
        <meta name="twitter:image" content={`${SITE_URL}/lac_logo_gold_transparent.png`} />
        
        {/* Additional SEO */}
        <meta name="author" content="ActionFraudOne" />
        <meta name="keywords" content={`is ${entity.name} a scam, ${entity.name} review, ${entity.name} legit, ${entity.name} fraud, scam check ${entity.type_label?.toLowerCase()}`} />
        
        {/* Schema.org JSON-LD - Multiple schemas for rich results */}
        {schema_markup?.webpage && (
          <script type="application/ld+json">{JSON.stringify(schema_markup.webpage)}</script>
        )}
        {schema_markup?.breadcrumb && (
          <script type="application/ld+json">{JSON.stringify(schema_markup.breadcrumb)}</script>
        )}
        {schema_markup?.faq && (
          <script type="application/ld+json">{JSON.stringify(schema_markup.faq)}</script>
        )}
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-28 pb-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0a0a] to-[#0a0a0a]" />
          <div className="absolute inset-0 opacity-[0.015]" style={{
            backgroundImage: 'linear-gradient(#C3A86B 1px, transparent 1px), linear-gradient(90deg, #C3A86B 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/" className="hover:text-gold">Home</Link>
              <ChevronRight size={14} />
              <Link to="/scam-database" className="hover:text-gold">Scam Database</Link>
              <ChevronRight size={14} />
              <span className="text-gold">{entity.name}</span>
            </nav>

            {/* Entity Type Badge */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2 bg-[#111] border border-gold/20 rounded-full px-4 py-2">
                <EntityIcon size={16} className="text-gold" />
                <span className="text-gold/90 text-sm font-medium">{entity.type_label}</span>
              </div>
              {entity.verified_scam && (
                <span className="bg-red-500/20 text-red-400 text-xs font-bold px-3 py-1 rounded-full border border-red-500/30">
                  VERIFIED SCAM
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              Is <span className="text-gold">{entity.name}</span> Legit or a Scam?
            </h1>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-4 text-sm text-gray-400">
              <span>First detected: {new Date(entity.first_seen).toLocaleDateString()}</span>
              <span>•</span>
              <span>{entity.scan_count} scans</span>
              <span>•</span>
              <span>{report_count} user reports</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto grid lg:grid-cols-3 gap-6">
            
            {/* Main Column */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Risk Rating Card */}
              <Card className="bg-[#111] border-gold/20">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    <div className={`w-24 h-24 ${styles.bg} rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                      {getRiskIcon(entity.risk_level)}
                    </div>
                    
                    <div className="flex-1 text-center sm:text-left">
                      <div className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold ${styles.bgLight} ${styles.text} border ${styles.border} mb-3`}>
                        {entity.risk_level?.toUpperCase()}
                      </div>
                      
                      <div className="flex items-center justify-center sm:justify-start gap-6 mt-2">
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wider">Risk Score</p>
                          <p className={`text-3xl font-bold ${styles.text}`}>{entity.risk_score}</p>
                        </div>
                        <div className="w-px h-10 bg-gold/20" />
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wider">Confidence</p>
                          <p className="text-3xl font-bold text-white">{entity.confidence_score}%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Scam Indicators */}
              {entity.red_flags?.length > 0 && (
                <Card className="bg-[#111] border-gold/20">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <AlertTriangle size={20} className="text-gold" />
                      Scam Indicators Detected
                    </h2>
                    <ul className="space-y-3">
                      {entity.red_flags.slice(0, 8).map((flag, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            flag.severity === 'high' ? 'bg-red-500' : 
                            flag.severity === 'medium' ? 'bg-yellow-500' : 'bg-gray-500'
                          }`} />
                          <span className="text-gray-300">{flag.description}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Risk Score Breakdown */}
              {entity.red_flags?.length > 0 && (
                <Card className="bg-[#111] border-gold/20">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Calculator size={20} className="text-gold" />
                      Risk Score Breakdown
                    </h2>
                    <div className="space-y-3">
                      {entity.red_flags.map((flag, i) => {
                        const { icon: IconComponent, color } = getCategoryIcon(flag.type);
                        return (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-gold/5 last:border-0">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <IconComponent size={16} className={color} />
                              </div>
                              <span className="text-gray-300 text-sm">{flag.category}</span>
                            </div>
                            <span className="text-gold font-mono font-bold text-sm">+{flag.weight}</span>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between pt-3 mt-2 border-t border-gold/20">
                        <span className="text-white font-semibold">Total Risk Score</span>
                        <span className="text-gold font-mono font-bold text-lg">{entity.risk_score}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recommended Action */}
              <Card className="bg-gold/5 border-gold/30">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-gold mb-3">Recommended Action</h2>
                  <p className="text-gray-300 leading-relaxed">{entity.recommended_action}</p>
                </CardContent>
              </Card>

              {/* User Reports */}
              {recent_reports?.length > 0 && (
                <Card className="bg-[#111] border-gold/20">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Users size={20} className="text-gold" />
                      User Reports ({report_count})
                    </h2>
                    <div className="space-y-4">
                      {recent_reports.map((report, i) => (
                        <div key={i} className="bg-black/40 rounded-lg p-4 border border-gold/10">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-gold text-sm font-semibold">{report.scam_type}</span>
                            {report.country && (
                              <span className="text-gray-500 text-xs">• {report.country}</span>
                            )}
                          </div>
                          {report.description && (
                            <p className="text-gray-400 text-sm">{report.description.slice(0, 200)}...</p>
                          )}
                          <p className="text-gray-500 text-xs mt-2">
                            Reported: {new Date(report.reported_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Disclaimer */}
              <Card className="bg-[#0a0a0a] border-gray-800">
                <CardContent className="p-5">
                  <p className="text-gray-500 text-sm leading-relaxed">
                    <strong className="text-gray-400">Disclaimer:</strong> This page provides automated fraud-risk 
                    analysis based on user submissions and system detection signals. It does not constitute a 
                    legal determination. Always conduct independent verification before engaging in financial transactions.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              
              {/* Report This Entity */}
              <Card className="bg-[#111] border-gold/20">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white mb-3">Have Information?</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Help others by submitting a report about this entity.
                  </p>
                  <Button 
                    onClick={() => setShowReportForm(!showReportForm)}
                    className="w-full bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30 font-semibold"
                  >
                    <Flag className="mr-2" size={16} />
                    Submit Report
                  </Button>
                </CardContent>
              </Card>

              {/* Report Form */}
              {showReportForm && (
                <Card className="bg-[#111] border-gold/30">
                  <CardContent className="p-5">
                    <h4 className="text-white font-semibold mb-4">Report This Entity</h4>
                    <form onSubmit={handleReportSubmit} className="space-y-4">
                      <div>
                        <Label className="text-gray-300 text-sm">Scam Type *</Label>
                        <select
                          value={reportForm.scam_type}
                          onChange={(e) => setReportForm(prev => ({ ...prev, scam_type: e.target.value }))}
                          className="w-full bg-black/60 border border-gold/20 text-white rounded-md px-3 py-2 mt-1"
                          required
                          data-testid="report-scam-type-select"
                        >
                          <option value="">Select type</option>
                          {SCAM_TYPES.map((t, i) => (
                            <option key={i} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-gray-300 text-sm">Description</Label>
                        <Textarea
                          value={reportForm.description}
                          onChange={(e) => setReportForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Describe your experience..."
                          className="bg-black/60 border-gold/20 text-white min-h-[80px] mt-1"
                          data-testid="report-description-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-300 text-sm">Country</Label>
                          <Input
                            value={reportForm.country}
                            onChange={(e) => setReportForm(prev => ({ ...prev, country: e.target.value }))}
                            placeholder="UK"
                            className="bg-black/60 border-gold/20 text-white mt-1"
                            data-testid="report-country-input"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300 text-sm">Amount Lost</Label>
                          <Input
                            value={reportForm.amount_lost}
                            onChange={(e) => setReportForm(prev => ({ ...prev, amount_lost: e.target.value }))}
                            placeholder="£500"
                            className="bg-black/60 border-gold/20 text-white mt-1"
                            data-testid="report-amount-input"
                          />
                        </div>
                      </div>
                      <Button
                        type="submit"
                        disabled={submittingReport}
                        className="w-full bg-gold hover:bg-gold/90 text-black font-bold"
                        data-testid="report-submit-btn"
                      >
                        {submittingReport ? 'Submitting...' : 'Submit Report'}
                      </Button>
                      <p className="text-gray-500 text-xs text-center">
                        Your report is anonymous and helps protect others.
                      </p>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Share This Warning */}
              <Card className="bg-[#111] border-gold/20">
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                    <Share2 size={18} className="text-gold" />
                    Share This Warning
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Help protect others by sharing this scam alert.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <ShareButton
                      icon={Twitter}
                      label="Twitter"
                      onClick={() => handleShare('twitter')}
                      className="text-white"
                      bgColor="#1DA1F2"
                    />
                    <ShareButton
                      icon={Facebook}
                      label="Facebook"
                      onClick={() => handleShare('facebook')}
                      className="text-white"
                      bgColor="#4267B2"
                    />
                    <ShareButton
                      icon={MessageCircle}
                      label="WhatsApp"
                      onClick={() => handleShare('whatsapp')}
                      className="text-white"
                      bgColor="#25D366"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 bg-gold/20 text-gold border border-gold/30"
                      data-testid="share-copy-btn"
                    >
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                      <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>
                  <p className="text-gray-500 text-xs text-center mt-3">
                    Sharing helps build a safer community
                  </p>
                </CardContent>
              </Card>

              {/* Run Your Own Check */}
              <Card className="bg-gold/5 border-gold/30">
                <CardContent className="p-5 text-center">
                  <Search size={32} className="text-gold mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">Run Your Own Check</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Analyse suspicious messages, URLs, or emails instantly.
                  </p>
                  <Link to="/scam-checker">
                    <Button className="w-full bg-gold hover:bg-gold/90 text-black font-bold">
                      Open Scanner
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Need Investigation */}
              <Card className="bg-[#111] border-gold/20">
                <CardContent className="p-5 text-center">
                  <Shield size={32} className="text-gold mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-white mb-2">Lost Money?</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Our specialists can investigate and help with recovery.
                  </p>
                  <Link to="/fraud-investigation">
                    <Button variant="outline" className="w-full border-gold/40 text-gold hover:bg-gold/10 font-semibold">
                      Request Investigation
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScamCheckPage;
